import { apiAction, promiseApiAction } from 'actions/application'
import { createAction } from 'actions/utils'
import { pageErrorHandler, createModalErrorHandler } from 'sagas/errorHandlers'
import {
  getGroups,
  getGroup,
  addCollection,
  updateCollection,
  updateCollectionMetadata,
  activateCollection,
  obsoleteCollection,
  duplicateCollection,
  getCollectionFeaturesByIds,
} from 'services/collections'

export const GET_COLLECTIONS_SUCCESS = 'GET_COLLECTIONS_SUCCESS'
export const GET_COLLECTIONS_FAILURE = 'GET_COLLECTIONS_FAILURE'
export const GET_COLLECTIONS_REQUEST = 'GET_COLLECTIONS_REQUEST'
export const getCollections = ({ groupType, vehicleSubType }) =>
  apiAction({
    asyncFn: getGroups,
    successActionType: GET_COLLECTIONS_SUCCESS,
    failureActionType: GET_COLLECTIONS_FAILURE,
    requestActionType: GET_COLLECTIONS_REQUEST,
    id: `${groupType}-${vehicleSubType}`,
    args: { groupType, vehicleSubType },
  })

export const GET_COLLECTION_SUCCESS = 'GET_COLLECTION_SUCCESS'
export const GET_COLLECTION_FAILURE = 'GET_COLLECTION_FAILURE'
export const GET_COLLECTION_REQUEST = 'GET_COLLECTION_REQUEST'
export const getCollection = (collection, serialNumber) => {
  const { type: groupType, id } = collection
  return apiAction({
    asyncFn: getGroup,
    successActionType: GET_COLLECTION_SUCCESS,
    failureActionType: GET_COLLECTION_FAILURE,
    requestActionType: GET_COLLECTION_REQUEST,
    id: `${groupType}-${id}-${serialNumber}`,
    args: { groupType, id, collection, serialNumber },
    // The FeatureCollectionDetails page renders its own inline error Alert
    // (driven by GET_COLLECTION_FAILURE → reducer sets fetchError: true).
    // pageErrorHandler suppresses the blocking ErrorModal so only the
    // inline error UI is shown, preserving the Retry button UX.
    errorHandlers: [pageErrorHandler],
  })
}

export const getCollectionsSuccess = createAction(GET_COLLECTIONS_SUCCESS, [
  'data',
])

export const CREATE_COLLECTION_SUCCESS = 'CREATE_COLLECTION_SUCCESS'
export const CREATE_COLLECTION_FAILURE = 'CREATE_COLLECTION_FAILURE'
export const postCollection = ({
  title,
  description,
  vehicleSubType,
  partUris,
  serialNumber,
  collectionType,
  varMinCompat,
  mdlDbCd,
  buildDate,
  onError,
}) =>
  apiAction({
    asyncFn: addCollection,
    args: {
      title,
      description,
      vehicleSubType,
      partUris,
      collectionType,
      varMinCompat,
      mdlDbCd,
      buildDate,
    },
    successActionType: CREATE_COLLECTION_SUCCESS,
    failureActionType: CREATE_COLLECTION_FAILURE,
    id: serialNumber,
    // When onError is provided the error is shown inline inside the modal via
    // ModalErrorContent rather than via a global overlay.
    errorHandlers: onError ? [createModalErrorHandler(onError)] : [],
  })

export const UPDATE_COLLECTION_SUCCESS = 'UPDATE_COLLECTION_SUCCESS'
export const UPDATE_COLLECTION_FAILURE = 'UPDATE_COLLECTION_FAILURE'
export const UPDATE_COLLECTION_REQUEST = 'UPDATE_COLLECTION_REQUEST'
export const updateCollectionAction = ({
  id,
  addPartUris,
  removePartUris,
  name,
  description,
}) =>
  apiAction({
    asyncFn: updateCollection,
    args: { id, addPartUris, removePartUris, name, description },
    successActionType: UPDATE_COLLECTION_SUCCESS,
    failureActionType: UPDATE_COLLECTION_FAILURE,
    requestActionType: UPDATE_COLLECTION_REQUEST,
    id,
  })

export const updateCollectionPromise = ({
  id,
  addPartUris,
  removePartUris,
  name,
  description,
}) =>
  promiseApiAction({
    asyncFn: updateCollection,
    args: { id, addPartUris, removePartUris, name, description },
    successActionType: UPDATE_COLLECTION_SUCCESS,
    failureActionType: UPDATE_COLLECTION_FAILURE,
    requestActionType: UPDATE_COLLECTION_REQUEST,
    id,
  })

export const UPDATE_COLLECTION_METADATA_SUCCESS =
  'UPDATE_COLLECTION_METADATA_SUCCESS'
export const UPDATE_COLLECTION_METADATA_FAILURE =
  'UPDATE_COLLECTION_METADATA_FAILURE'
export const updateCollectionMetadataAction = ({
  id,
  name,
  description,
  varMinCompat,
  mdlDbCd,
}) =>
  apiAction({
    asyncFn: updateCollectionMetadata,
    args: { id, name, description, varMinCompat, mdlDbCd },
    successActionType: UPDATE_COLLECTION_METADATA_SUCCESS,
    failureActionType: UPDATE_COLLECTION_METADATA_FAILURE,
    id,
  })

export const CREATE_COLLECTION_NOTIFICATION = 'CREATE_COLLECTION_NOTIFICATION'
export const createCollectionNotification = createAction(
  CREATE_COLLECTION_NOTIFICATION,
  ['message', 'notificationType']
)

export const CLEAR_CREATE_COLLECTION_NOTIFICATION =
  'CLEAR_CREATE_COLLECTION_NOTIFICATION'
export const clearCreateCollectionNotificaiton = createAction(
  CLEAR_CREATE_COLLECTION_NOTIFICATION
)

export const RESET_COLLECTIONS = 'RESET_COLLECTIONS'
export const resetCollections = createAction(RESET_COLLECTIONS)

export const RESET_COLLECTION = 'RESET_COLLECTION'
export const resetCollection = createAction(RESET_COLLECTION)

export const CLEAR_CREATED_COLLECTION = 'CLEAR_CREATED_COLLECTION'
export const clearCreatedCollection = createAction(CLEAR_CREATED_COLLECTION)

// PBI 150666: Clear status change notification
export const CLEAR_STATUS_CHANGE_NOTIFICATION =
  'CLEAR_STATUS_CHANGE_NOTIFICATION'
export const clearStatusChangeNotification = createAction(
  CLEAR_STATUS_CHANGE_NOTIFICATION
)

// PBI 150666: Activation validation error (obsolete features)
export const SET_ACTIVATION_VALIDATION_ERROR = 'SET_ACTIVATION_VALIDATION_ERROR'
export const setActivationValidationError = createAction(
  SET_ACTIVATION_VALIDATION_ERROR,
  ['error']
)

export const CLEAR_ACTIVATION_VALIDATION_ERROR =
  'CLEAR_ACTIVATION_VALIDATION_ERROR'
export const clearActivationValidationError = createAction(
  CLEAR_ACTIVATION_VALIDATION_ERROR
)

// Collection Lifecycle Actions (PBI 150666)
// TODO: Backend API not yet available - these actions are ready for integration

export const ACTIVATE_COLLECTION_REQUEST = 'ACTIVATE_COLLECTION_REQUEST'
export const ACTIVATE_COLLECTION_SUCCESS = 'ACTIVATE_COLLECTION_SUCCESS'
export const ACTIVATE_COLLECTION_FAILURE = 'ACTIVATE_COLLECTION_FAILURE'
export const activateCollectionAction = ({ id }) =>
  apiAction({
    asyncFn: activateCollection,
    successActionType: ACTIVATE_COLLECTION_SUCCESS,
    failureActionType: ACTIVATE_COLLECTION_FAILURE,
    requestActionType: ACTIVATE_COLLECTION_REQUEST,
    id,
    args: { id },
  })

export const OBSOLETE_COLLECTION_REQUEST = 'OBSOLETE_COLLECTION_REQUEST'
export const OBSOLETE_COLLECTION_SUCCESS = 'OBSOLETE_COLLECTION_SUCCESS'
export const OBSOLETE_COLLECTION_FAILURE = 'OBSOLETE_COLLECTION_FAILURE'
export const obsoleteCollectionAction = ({ id }) =>
  apiAction({
    asyncFn: obsoleteCollection,
    successActionType: OBSOLETE_COLLECTION_SUCCESS,
    failureActionType: OBSOLETE_COLLECTION_FAILURE,
    requestActionType: OBSOLETE_COLLECTION_REQUEST,
    id,
    args: { id },
  })

export const DUPLICATE_COLLECTION_REQUEST = 'DUPLICATE_COLLECTION_REQUEST'
export const DUPLICATE_COLLECTION_SUCCESS = 'DUPLICATE_COLLECTION_SUCCESS'
export const DUPLICATE_COLLECTION_FAILURE = 'DUPLICATE_COLLECTION_FAILURE'
export const duplicateCollectionAction = ({ id }) =>
  apiAction({
    asyncFn: duplicateCollection,
    successActionType: DUPLICATE_COLLECTION_SUCCESS,
    failureActionType: DUPLICATE_COLLECTION_FAILURE,
    requestActionType: DUPLICATE_COLLECTION_REQUEST,
    id,
    args: { id },
  })

// Bug 479018: Clear duplicated collection state after navigation to prevent crash loop
export const CLEAR_DUPLICATED_COLLECTION = 'CLEAR_DUPLICATED_COLLECTION'
export const clearDuplicatedCollection = createAction(
  CLEAR_DUPLICATED_COLLECTION
)

// ── Collection features by ID ─────────────────────────────────────────────────
// Fetches features at pinned revisions for a collection, bypassing the standard
// search reducer which would normalize features to their latest revision.
// Stored in the collection reducer under `collectionFeatures`.

export const COLLECTION_FEATURES_REQUEST = 'COLLECTION_FEATURES_REQUEST'
export const COLLECTION_FEATURES_RECEIVED = 'COLLECTION_FEATURES_RECEIVED'
export const COLLECTION_FEATURES_FAILED = 'COLLECTION_FEATURES_FAILED'

/**
 * Fetch collection features by their IDs, preserving pinned revisions.
 *
 * Uses pageErrorHandler to suppress the blocking ErrorModal on failure.
 * The inline error state in FeatureCollectionDetails is driven by the
 * COLLECTION_FEATURES_FAILED action → reducer sets collectionFeaturesError.
 *
 * Short-circuits with an empty success action when ids is empty to avoid
 * sending a request with no IDs (which produces an unfiltered or 400 response).
 *
 * @param {{ ids: string[], vehicleSubType?: string, mdlDbCd?: string, buildDate?: string }} params
 */
export const fetchCollectionFeatures = ({
  ids,
  collectionId,
  vehicleSubType,
  mdlDbCd,
  buildDate,
}) => {
  if (!ids || ids.length === 0) {
    return {
      type: COLLECTION_FEATURES_RECEIVED,
      data: { features: [] },
      // ids at the top level matches the shape apiCall.js produces via `...args`
      // spread, so the reducer's `action.ids || []` guard works consistently
      // regardless of which path dispatched COLLECTION_FEATURES_RECEIVED.
      ids: [],
      collectionId,
      args: { ids: [] },
    }
  }

  return apiAction({
    asyncFn: getCollectionFeaturesByIds,
    args: { ids, collectionId, vehicleSubType, mdlDbCd, buildDate },
    successActionType: COLLECTION_FEATURES_RECEIVED,
    failureActionType: COLLECTION_FEATURES_FAILED,
    requestActionType: COLLECTION_FEATURES_REQUEST,
    // Include all vehicle context parameters in the de-dupe key so that
    // requests for the same feature IDs in different vehicle contexts
    // (vehicleSubType, mdlDbCd, buildDate) are not treated as duplicates.
    // Without context, the second request would be silently skipped and the
    // first context's cached results would be used for a different vehicle.
    id: [ids.join(','), vehicleSubType, mdlDbCd, buildDate]
      .filter(Boolean)
      .join('|'),
    errorHandlers: [pageErrorHandler],
  })
}
